import React from "react";
import Latex from "react-latex-next";
import { BlockMath } from "react-katex";
import GraphViewer from "../../components/GraphPlotter/GraphViewer";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";

const pointStyle = (x, y, label) => ({
  x,
  y,
  label,
  color: "#cc5500",
  textColor: "#cc5500",
  projectionColor: "#cc5500",
  projectionLabelOffsetX: 0.75,
  projectionLabelOffsetY: 0.6,
});

function AnalyticDiagram({ points, segments, showProjections = true }) {
  return (
    <div style={{ maxWidth: "350px", margin: "24px auto" }}>
      <GraphViewer points={points} segments={segments} showProjections={showProjections} />
    </div>
  );
}

const examples = [
  {
    label: "Przykład 1",
    summary: "Długość odcinka",
    description: <Latex>{`Oblicz długość odcinka $AB$, gdzie $A=(1,2)$, $B=(4,5)$.`}</Latex>,
    points: {
      A: pointStyle(1, 2, "A"),
      B: pointStyle(4, 5, "B"),
    },
    segments: [{ p1: "A", p2: "B", color: "#ff8c1a", markerColor: "#cc5500", width: 6 }],
    graphWidth: 350,
    content: (
      <div className="space-y-2">
        <p><strong>Krok 1.</strong> Korzystamy ze wzoru na długość odcinka.</p>
        <BlockMath math={String.raw`|AB|=\sqrt{(x_B-x_A)^2+(y_B-y_A)^2}`} />
        <p><strong>Krok 2.</strong> Podstawiamy współrzędne punktów.</p>
        <BlockMath math={String.raw`|AB|=\sqrt{(4-1)^2+(5-2)^2}=\sqrt{18}=3\sqrt{2}`} />
      </div>
    ),
  },
  {
    label: "Przykład 2",
    summary: "Środek odcinka",
    description: <Latex>{`Wyznacz środek odcinka $AB$, gdzie $A=(1,1)$ i $B=(5,3)$.`}</Latex>,
    points: {
      A: pointStyle(1, 1, "A"),
      B: pointStyle(5, 3, "B"),
      S: pointStyle(3, 2, "S"),
    },
    segments: [{ p1: "A", p2: "B", color: "#ff8c1a", markerColor: "#cc5500", width: 6 }],
    graphWidth: 350,
    content: (
      <div className="space-y-2">
        <p><strong>Krok 1.</strong> Liczymy średnią obu współrzędnych.</p>
        <BlockMath math={String.raw`S=\left(\frac{x_A+x_B}{2},\frac{y_A+y_B}{2}\right)`} />
        <BlockMath math={String.raw`S=\left(\frac{1+5}{2},\frac{1+3}{2}\right)=(3,2)`} />
      </div>
    ),
  },
  {
    label: "Przykład 3",
    summary: "Brakujący koniec odcinka",
    description: <Latex>{`Punkt $B=(2,-2)$ i środek $S=(-1,1)$ są dane. Wyznacz punkt $A$.`}</Latex>,
    points: {
      A: pointStyle(-4, 4, "A"),
      B: pointStyle(2, -2, "B"),
      S: pointStyle(-1, 1, "S"),
    },
    segments: [{ p1: "A", p2: "B", color: "#ff8c1a", markerColor: "#cc5500", width: 6 }],
    graphWidth: 350,
    content: (
      <div className="space-y-2">
        <p><strong>Krok 1.</strong> Korzystamy ze wzoru na środek i rozwiązujemy dwa równania.</p>
        <BlockMath math={String.raw`\frac{x_A+2}{2}=-1,\qquad \frac{y_A-2}{2}=1`} />
        <BlockMath math={String.raw`x_A=-4,\qquad y_A=4,\qquad A=(-4,4)`} />
      </div>
    ),
  },
];

export default function AnalyticGeometryPreparationTheory() {
  const distanceDiagram = {
    A: pointStyle(1, 1, "A"),
    B: pointStyle(5, 4, "B"),
  };
  const midpointDiagram = {
    A: pointStyle(1, 1, "A"),
    B: pointStyle(5, 4, "B"),
    S: pointStyle(3, 2.5, "S"),
  };
  const segment = [{ p1: "A", p2: "B", color: "#ff8c1a", markerColor: "#cc5500", width: 6 }];

  const intro = (
    <div className="space-y-3">
      <p>
        Długość odcinka <Latex>$AB$</Latex> wyznaczamy na podstawie współrzędnych jego końców:
      </p>
      <BlockMath math={String.raw`|AB|=\sqrt{(x_B-x_A)^2+(y_B-y_A)^2}`} />
      <p>Różnice współrzędnych tworzą przyprostokątne pomocniczego trójkąta prostokątnego.</p>
      <AnalyticDiagram points={distanceDiagram} segments={segment} />
      <p>
        Współrzędne środka <Latex>$S$</Latex> to średnie arytmetyczne odpowiednich współrzędnych końców odcinka:
      </p>
      <BlockMath math={String.raw`S=\left(\frac{x_A+x_B}{2},\frac{y_A+y_B}{2}\right)`} />
      <AnalyticDiagram points={midpointDiagram} segments={segment} />
    </div>
  );

  return <TheoryExamplesRunner intro={intro} examples={examples} showCoordinates />;
}
